import * as CL from '@design-system/component-library';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { emailMsg, invoiceLanguageMsg, recipientMsg, t } from '../../common/i18n/index.js';
import {
  FORM_GRID_FULL_WIDTH,
  FormGridFieldset,
} from '../../common/react-hook-form/FormGridFieldset/FormGridFieldset.js';
import { ListInput } from '../../common/react-hook-form/components/ListInput.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { Contact } from '../../common/react-hook-form/fields/Contact.js';
import { DeliveryMethod } from '../../common/react-hook-form/fields/DeliveryMethod.js';
import { ElectronicAddress } from '../../common/react-hook-form/fields/ElectronicAddress.js';
import { ElectronicOperator } from '../../common/react-hook-form/fields/ElectronicOperator.js';
import { Language } from '../../common/react-hook-form/fields/Language.js';
import { ReceiverTypes, getDefaultElectronicOperator } from '../../common/utils/billingAccountUtils.js';
import { BillingAccountDeliveryMethod } from '../../generated/api/billingAccountDeliveryMethod.js';
import { ContactType } from '../../generated/api/contactType.js';
import type { EOperatorItem } from '../../common/utils/billingAccountUtils.js';
import type { BillChannel } from '../../generated/api/billChannel.js';
import type { Contact as ContactEntityType } from '../../generated/api/contact.js';

export const deliveryMethodDefaultValues = (billChannels?: BillChannel[]) => {
  const defaultOperatorItem = getDefaultElectronicOperator(billChannels);
  const defaultElectronicAddress =
    defaultOperatorItem?.active && defaultOperatorItem?.electronicAddress ? defaultOperatorItem.electronicAddress : '';

  return {
    billElectronicAddress: defaultElectronicAddress,
    billElectronicOperator: defaultOperatorItem?.id || '',
    billLanguage: 'FI',
    deliveryMethod: BillingAccountDeliveryMethod.ELECTRONIC,
    billReceiverType: ContactType.PERSON,
    billReceiverName: '',
    additionalBillReceiverEmails: [],
  };
};

interface DeliveryMethodFieldSetProps {
  billChannels: BillChannel[];
  contacts: ContactEntityType[];
  onCreateBillReceiverContact: () => void;
}

export const DeliveryMethodFieldset = (props: DeliveryMethodFieldSetProps) => {
  const methods = useFormContext();
  const values = methods.watch();
  const { setValue, setError } = methods;

  const handleOperatorChange = (selectedOperator?: EOperatorItem) => {
    if (selectedOperator?.active && selectedOperator?.electronicAddress) {
      setValue('billingAccount.billElectronicAddress', selectedOperator.electronicAddress);
    } else {
      setValue('billingAccount.billElectronicAddress', '');
    }
    setError('billingAccount.billElectronicAddress', { message: undefined });
  };

  return (
    <FormGridFieldset>
      <DeliveryMethod name="billingAccount.deliveryMethod" billChannels={props.billChannels} />
      <Language name="billingAccount.billLanguage" label={t.A7DR(invoiceLanguageMsg)} />

      {values.billingAccount.deliveryMethod === BillingAccountDeliveryMethod.ELECTRONIC && (
        <>
          <ElectronicOperator
            name="billingAccount.billElectronicOperator"
            billChannels={props.billChannels}
            onOperatorChange={handleOperatorChange}
          />
          <ElectronicAddress name="billingAccount.billElectronicAddress" />
        </>
      )}
      {values.billingAccount.deliveryMethod === BillingAccountDeliveryMethod.EMAIL && (
        <>
          <div>
            <SelectRadio
              name="billReceiverSelection"
              label={t.SY1D(recipientMsg)}
              items={[
                {
                  label: t.ZCX7('Same as contact'),
                  value: ReceiverTypes.SAME_AS_CONTACT,
                },
                {
                  label: t.C6WY('Other'),
                  value: ReceiverTypes.OTHER,
                },
              ]}
            />
          </div>
          <div />
          {values.billReceiverSelection === ReceiverTypes.OTHER && (
            <>
              <Contact
                name="billingAccount.billReceiverId"
                contacts={props.contacts}
                reference="receiver"
                onCreateContact={props.onCreateBillReceiverContact}
              />
              <div />
              <div />
              <div />
            </>
          )}
          <div>
            <h4>{t.UZ3K('Other receiver')}</h4>
          </div>
          <div />
          <ListInput label={t.OKYY(emailMsg)} name="billingAccount.additionalBillReceiverEmails" />
          {(values.billingAccount.additionalBillReceiverEmails || []).length < 5 && (
            <div className={FORM_GRID_FULL_WIDTH}>
              <CL.Button
                className={dsClass.MARGIN_BOTTOM_4}
                onClick={() => {
                  methods.setValue('billingAccount.additionalBillReceiverEmails', [
                    ...(values.billingAccount.additionalBillReceiverEmails || []),
                    '',
                  ]);
                }}
                color="light"
              >
                {t.N41Q('+ Add new')}
              </CL.Button>
            </div>
          )}
        </>
      )}
    </FormGridFieldset>
  );
};
